import React, { useEffect, useState } from 'react'
import {
  ClassesSelectedsSpan,
  ClassesWrapper,
  InfoUserWrapper,
  InfoWrapper,
  MyAccessCodeWrapper,
  InfoJobPositionAndRoleWrapper,
  ClassesSelectionContainer,
  ClassesListTitle
} from './MyConfig.styles'
import { useTranslation } from 'i18n'
import type { LanguageTokens } from 'i18n'
import type { User } from 'api/data.types'
import { Input, FontIcon, tokens, Title } from '@arvoreeducacao/bonsai-ds'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  teachersControllerGetTeacherClasses,
  teachersControllerSetTeacherClasses,
  entitiesControllerGetEntityTree
} from 'api.gen'
import type { EntityTreeNode } from 'api.gen'
import { useLocation } from 'react-router-dom'
import scrollTo from 'utils/scrollTo'
import useFeatureToggles from 'utils/hooks/use-feature-toggles'
import ListJobPositionsAndRoleMyConfig from './list-job-positions-and-role-my-config'
import TeacherClassHierarchySelector from './TeacherClassHierarchySelector'
import SelectedClassesTree from './SelectedClassesTree'

interface MyConfigProps {
  user: User
}

function findPathToClass(
  node: EntityTreeNode,
  classId: number,
  currentPath: Array<{ id: number; name: string; type: string }>
): Array<{ id: number; name: string; type: string }> | null {
  const newPath = [
    ...currentPath,
    {
      id: Number(node.id),
      name: node.name,
      type: node.entity_type
    }
  ]

  if (node.entity_type === 'class' && Number(node.id) === classId) {
    return newPath
  }

  if (node.children) {
    for (const child of node.children) {
      const result = findPathToClass(child, classId, newPath)
      if (result) return result
    }
  }

  return null
}

const MyConfig = ({ user }: MyConfigProps) => {
  const queryClient = useQueryClient()
  const isTeacher = Boolean(user?.teacher)
  const teacherId = user?.teacher?.id ? Number(user.teacher.id) : undefined
  const { featureToggles } = useFeatureToggles()
  const canAccessTeacherClassDropdown =
    isTeacher && !featureToggles?.paranaNotice

  const location = useLocation()
  const [deletingIds, setDeletingIds] = useState<number[]>([])
  const [classHierarchies, setClassHierarchies] = useState<
    Record<number, Array<{ id: number; name: string; type: string }>>
  >({})

  const { data: teacherClassesData, isLoading: isLoadingTeacherClasses } =
    useQuery({
      queryKey: ['teacher-classes', teacherId],
      queryFn: () =>
        teacherId
          ? teachersControllerGetTeacherClasses(teacherId)
          : Promise.reject(new Error('No teacher ID')),
      enabled: !!teacherId
    })
  const teacherClasses = teacherClassesData?.data?.classes ?? []

  const { mutate: setTeacherClasses } = useMutation({
    mutationFn: (classIds: number[]) => {
      if (!teacherId) {
        return Promise.reject(new Error('No teacher ID'))
      }
      return teachersControllerSetTeacherClasses(teacherId, { classIds })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['teacher-classes', teacherId]
      })
    }
  })

  useEffect(() => {
    if (
      location.hash &&
      document.getElementById(location.hash.replace('#', '')) &&
      !isLoadingTeacherClasses
    ) {
      scrollTo(
        document.documentElement,
        document
          .getElementById(location.hash.replace('#', ''))
          .getBoundingClientRect().top - 100,
        300
      )
    }
  }, [location, isLoadingTeacherClasses])

  useEffect(() => {
    const loadHierarchies = async () => {
      if (!isLoadingTeacherClasses && teacherClasses.length > 0) {
        try {
          const treeResult = await entitiesControllerGetEntityTree()
          const rootNode = treeResult.data?.root

          if (!rootNode) return

          const hierarchies: Record<
            number,
            Array<{ id: number; name: string; type: string }>
          > = {}

          for (const teacherClass of teacherClasses) {
            if (!classHierarchies[teacherClass.classId]) {
              const path = findPathToClass(rootNode, teacherClass.classId, [])

              if (path) {
                hierarchies[teacherClass.classId] = path
              }
            }
          }

          if (Object.keys(hierarchies).length > 0) {
            setClassHierarchies(prev => ({ ...prev, ...hierarchies }))
          }
        } catch (error) {
          console.error('Error loading hierarchies:', error)
        }
      }
    }

    loadHierarchies()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingTeacherClasses, teacherClasses.length])

  function handleClassToggle(
    classId: number,
    checked: boolean,
    hierarchyPath?: Array<{ id: number; name: string; type: string }>
  ) {
    if (checked) {
      const currentIds = teacherClasses.map(c => c.classId)
      if (!currentIds.includes(classId)) {
        setTeacherClasses([...currentIds, classId])
        if (hierarchyPath) {
          setClassHierarchies(prev => ({
            ...prev,
            [classId]: hierarchyPath
          }))
        }
      }
    } else {
      setDeletingIds(prev => [...prev, classId])
      const currentIds = teacherClasses.map(c => c.classId)
      setTeacherClasses(currentIds.filter(id => id !== classId))
      setClassHierarchies(prev => {
        const newHierarchies = { ...prev }
        delete newHierarchies[classId]
        return newHierarchies
      })
    }
  }

  function handleClassRemoval(classId: number) {
    setDeletingIds(prev => [...prev, classId])
    const currentIds = teacherClasses.map(c => c.classId)
    setTeacherClasses(currentIds.filter(id => id !== classId))
  }

  const { t } = useTranslation()

  return (
    <MyAccessCodeWrapper>
      <InfoWrapper>
        <h5>{t('Nome')}</h5>
        <InfoUserWrapper>
          <FontIcon iconName="user" size={16} color={tokens.colors.dark5} />
          <p>{user.name}</p>
        </InfoUserWrapper>
      </InfoWrapper>
      {isTeacher && featureToggles?.newPermissionsManagement && (
        <InfoJobPositionAndRoleWrapper>
          <Title text={t('Cargos e Permissões')} size={5} />
          <ListJobPositionsAndRoleMyConfig />
        </InfoJobPositionAndRoleWrapper>
      )}
      <InfoWrapper>
        <h5>{t('E-mail')}</h5>
        <p>
          {t(
            'Usaremos esse endereço para enviar comunicações importantes. Acesse a área de notificações para escolher o que deseja receber.'
          )}
        </p>
        <Input label={t('E-mail')} defaultValue={user.email} disabled />
      </InfoWrapper>
      <InfoWrapper>
        <h5>{t('Unidade')}</h5>
        <InfoUserWrapper>
          <FontIcon iconName="school" size={16} color={tokens.colors.dark5} />
          <p>{user.reader.root.name}</p>
        </InfoUserWrapper>
      </InfoWrapper>
      {canAccessTeacherClassDropdown && (
        <InfoWrapper id="selecione-as-turmas">
          <h5>{t('Selecione as turmas que você quer acompanhar')}</h5>
          <p>
            {t(
              'Estas turmas personalizam parte da sua experiência. Avisaremos sempre que forem usadas, permitindo que você altere a seleção se desejar.'
            )}
          </p>
          {!isLoadingTeacherClasses && (
            <ClassesSelectionContainer>
              <div style={{ flex: 1 }}>
                <TeacherClassHierarchySelector
                  selectedClassIds={teacherClasses.map(c => c.classId)}
                  onClassToggle={handleClassToggle}
                />
              </div>

              <ClassesWrapper>
                <ClassesListTitle>{t('Turmas selecionadas')}</ClassesListTitle>
                {teacherClasses.length > 0 ? (
                  <>
                    <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      <SelectedClassesTree
                        teacherClasses={teacherClasses}
                        classHierarchies={classHierarchies}
                        deletingIds={deletingIds}
                        onClassRemove={handleClassRemoval}
                      />
                    </div>
                    <ClassesSelectedsSpan>
                      {t('{{count}} turma selecionada' as LanguageTokens, {
                        count: teacherClasses.length
                      })}
                    </ClassesSelectedsSpan>
                  </>
                ) : (
                  <div
                    style={{
                      padding: '24px',
                      textAlign: 'center',
                      color: tokens.colors.dark5,
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    Nenhuma turma selecionada
                  </div>
                )}
              </ClassesWrapper>
            </ClassesSelectionContainer>
          )}
        </InfoWrapper>
      )}
    </MyAccessCodeWrapper>
  )
}

export default MyConfig
